/* rv-reviews-carousel.js — arrows, autoplay, dots and the overflow guard for the reviews carousel (original code).
   The track is a native horizontal scroller (swipe / trackpad / keyboard work without JS). This script adds:
   - prev/next buttons that scroll by data-step px (loop: past the end → back to the start, before the start → to the end)
   - autoplay (data-autoplay / data-interval ms) that loops, paused while hovered, keyboard-focused, touched,
     off-screen, in a hidden tab, or when the visitor prefers reduced motion. Autoplay only runs when the track can
     scroll by at least one card: the measured 3-card desktop row overflows by just 10px, and advancing/looping over
     10px every 5 s would only jiggle the cards (the original shows no such motion), so that layout stays still while
     the arrows keep working. Re-evaluated on resize (a phone width overflows by whole cards and does autoplay).
   - a mouse click on an arrow does NOT end autoplay: the focus pause applies to keyboard focus (:focus-visible) and to
     focus inside the track, so a pointer-focused button lets autoplay resume once the pointer leaves the section
   - optional dots (one per card) kept in sync with the scroll position
   - an overflow guard: on desktop the row is centred; when more cards than fit are present the centred-with-overflow
     flex layout would hide the leftmost cards, so the track gets a modifier class that left-aligns it. The measured
     3-card desktop row overflows by 10px (5px each side, which the original leaves centred), so only overflow beyond
     one card gap flips the alignment.
   Card positions are measured with getBoundingClientRect relative to the track (not offsetLeft, whose origin is the
   nearest positioned ancestor — wrong as soon as the section is not at page x 0, e.g. in the theme editor).
   Re-initialises on shopify:section:load, stops timers on shopify:section:unload, scrolls to a block on
   shopify:block:select. */
(function () {
  var REDUCED = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var OVERFLOW_CLASS = 'rv-reviews-carousel__track--overflow';
  var DOT_ACTIVE = 'rv-reviews-carousel__dot--active';

  function isKeyboardFocus(el) {
    if (!el || !el.matches) return true;
    try { return el.matches(':focus-visible'); } catch (err) { return true; }
  }

  function setup(root) {
    if (root.rvReviewsCarousel) return root.rvReviewsCarousel;
    var track = root.querySelector('[data-rv-rc-track]');
    if (!track) return null;
    var prev = root.querySelector('[data-rv-rc-prev]');
    var next = root.querySelector('[data-rv-rc-next]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-rv-rc-dot]'));
    var cards = function () { return Array.prototype.slice.call(track.querySelectorAll('[data-rv-rc-card]')); };
    var step = parseInt(root.getAttribute('data-step'), 10) || 360;
    var interval = parseInt(root.getAttribute('data-interval'), 10) || 5000;
    var autoplay = root.getAttribute('data-autoplay') === 'true';
    var timer = null;
    var paused = { hover: false, focus: false, touch: false, hidden: false, offscreen: false };
    var gap = 0;

    function maxScroll() { return Math.max(0, track.scrollWidth - track.clientWidth); }
    function behavior() { return REDUCED.matches ? 'auto' : 'smooth'; }
    function padLeft() { return parseFloat(window.getComputedStyle(track).paddingLeft) || 0; }
    /* Left edge of a card in the track's scroll coordinates (0 = the track's left border edge at scrollLeft 0). */
    function cardLeft(card) {
      return card.getBoundingClientRect().left - track.getBoundingClientRect().left - track.clientLeft + track.scrollLeft;
    }
    function scrollTo(x) {
      x = Math.max(0, Math.min(maxScroll(), x));
      if (track.scrollTo) track.scrollTo({ left: x, behavior: behavior() });
      else track.scrollLeft = x;
    }
    function goNext() {
      var max = maxScroll();
      if (max <= 0) return;
      if (track.scrollLeft >= max - 1) scrollTo(0);
      else scrollTo(track.scrollLeft + step);
    }
    function goPrev() {
      var max = maxScroll();
      if (max <= 0) return;
      if (track.scrollLeft <= 1) scrollTo(max);
      else scrollTo(track.scrollLeft - step);
    }
    function goToCard(index) {
      var list = cards();
      var card = list[index];
      if (!card) return;
      scrollTo(cardLeft(card) - padLeft());
    }

    /* Overflow guard (desktop centring vs. reachable cards). */
    function updateOverflow() {
      var list = cards();
      if (list.length > 1) {
        gap = cardLeft(list[1]) - (cardLeft(list[0]) + list[0].offsetWidth);
      }
      var overflow = track.scrollWidth - track.clientWidth;
      track.classList.toggle(OVERFLOW_CLASS, overflow > Math.max(gap, 1));
    }

    /* Dots follow the card nearest to the track's left padding edge. */
    function updateDots() {
      if (!dots.length) return;
      var list = cards();
      var pos = track.scrollLeft + padLeft();
      var best = 0;
      var bestDist = Infinity;
      for (var i = 0; i < list.length; i++) {
        var d = Math.abs(cardLeft(list[i]) - pos);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      for (var j = 0; j < dots.length; j++) {
        var on = j === best;
        dots[j].classList.toggle(DOT_ACTIVE, on);
        if (on) dots[j].setAttribute('aria-current', 'true');
        else dots[j].removeAttribute('aria-current');
      }
    }

    /* Autoplay. hasRoom(): the track must be able to scroll by at least one card, otherwise advancing only jiggles. */
    function hasRoom() {
      var list = cards();
      var minScroll = list.length ? list[0].offsetWidth : step;
      return maxScroll() >= minScroll;
    }
    function shouldRun() {
      return autoplay && !REDUCED.matches && hasRoom() &&
        !paused.hover && !paused.focus && !paused.touch && !paused.hidden && !paused.offscreen;
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function start() {
      stop();
      if (!shouldRun()) return;
      timer = setInterval(function () { if (shouldRun()) goNext(); }, interval);
    }
    function pause(key, value) { paused[key] = value; start(); }

    if (prev) prev.addEventListener('click', function () { goPrev(); start(); });
    if (next) next.addEventListener('click', function () { goNext(); start(); });
    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        goToCard(parseInt(dot.getAttribute('data-rv-rc-dot'), 10) || 0);
        start();
      });
    });

    root.addEventListener('mouseenter', function () { pause('hover', true); });
    root.addEventListener('mouseleave', function () { pause('hover', false); });
    root.addEventListener('focusin', function (e) {
      /* Keyboard focus anywhere in the section, or any focus inside the track, pauses; a pointer-focused arrow does not. */
      pause('focus', track.contains(e.target) || isKeyboardFocus(e.target));
    });
    root.addEventListener('focusout', function (e) {
      if (!root.contains(e.relatedTarget)) pause('focus', false);
    });
    track.addEventListener('touchstart', function () { pause('touch', true); }, { passive: true });
    track.addEventListener('touchend', function () { pause('touch', false); }, { passive: true });
    track.addEventListener('touchcancel', function () { pause('touch', false); }, { passive: true });
    track.addEventListener('pointerdown', function (e) { if (e.pointerType !== 'mouse') pause('touch', true); });
    track.addEventListener('pointerup', function (e) { if (e.pointerType !== 'mouse') pause('touch', false); });

    var scrollTick = null;
    track.addEventListener('scroll', function () {
      if (scrollTick) return;
      scrollTick = window.requestAnimationFrame(function () { scrollTick = null; updateDots(); });
    }, { passive: true });

    var onVisibility = function () { pause('hidden', document.hidden === true); };
    document.addEventListener('visibilitychange', onVisibility);

    var observer = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { pause('offscreen', !entry.isIntersecting); });
      }, { threshold: 0 });
      observer.observe(root);
    }

    /* Resize: re-check the alignment guard, the dots and whether autoplay has room (start() stops or starts the timer). */
    var onResize = function () { updateOverflow(); updateDots(); start(); };
    window.addEventListener('resize', onResize);
    if (REDUCED.addEventListener) REDUCED.addEventListener('change', start);

    updateOverflow();
    updateDots();
    start();

    var api = {
      goToCard: goToCard,
      pause: function () { autoplay = false; stop(); },
      destroy: function () {
        stop();
        window.removeEventListener('resize', onResize);
        document.removeEventListener('visibilitychange', onVisibility);
        if (observer) observer.disconnect();
        root.rvReviewsCarousel = null;
      }
    };
    root.rvReviewsCarousel = api;
    return api;
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-rv-reviews-carousel]');
    Array.prototype.forEach.call(roots, setup);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector('[data-rv-reviews-carousel]') : null;
    if (root && root.rvReviewsCarousel) root.rvReviewsCarousel.destroy();
  });
  document.addEventListener('shopify:block:select', function (e) {
    var card = e.target && e.target.closest ? e.target.closest('[data-rv-rc-card]') : null;
    var root = card ? card.closest('[data-rv-reviews-carousel]') : null;
    if (!root) return;
    var api = setup(root);
    if (!api) return;
    api.pause();
    var list = Array.prototype.slice.call(root.querySelectorAll('[data-rv-rc-card]'));
    api.goToCard(list.indexOf(card));
  });
})();
